import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { scheduleBodyCatchUp } from "@/lib/mail/bodyCatchUp";
import { getPinnedInboxRowExitUids } from "@/lib/mail/inboxRowExit";
import { fetchInboxMessages, markMailMessageSeen, markMailMessageUnread } from "@/lib/api/imap";
import { markMailMessageFlagged } from "@/lib/api/mailLabels";
import {
  clearFolderSnapshot,
  getFolderSnapshot,
  isFolderSnapshotWarm,
  markFolderListViewedThisSession,
  patchFolderSnapshotSeen,
  patchFolderSnapshotRemoveUids,
  publishFolderSnapshot,
  subscribeFolderSnapshot,
  type FolderSnapshot,
} from "@/lib/mail/inboxFolderBus";
import {
  mergeHeaderLists,
  mergeInboxListsPreservingOrder,
  mergeRefreshBatch,
  mergeSnapshotWithLocal,
  sortInboxMessageList,
  idleDelay,
  syncFolderFromServerCache,
  syncFolderHeaders,
  warmFolderMessages,
} from "@/lib/mail/folderWarmup";
import {
  getPendingSeenValue,
  prunePendingSeenMap,
  reconcilePendingSeenEntries,
  setPendingSeenEntry,
  type PendingSeenEntry,
} from "@/lib/mail/mailSeenPending";
import {
  applyOptimisticMessages,
  filterHeldMutationMessages,
  hasMutationHold,
  tombstoneUids,
} from "@/lib/mail/mailMutationHold";
import { persistInboxHeadersSession } from "@/lib/cache/inboxHeadersSessionCache";
import { hydrateInboxHeadersFromSession } from "@/lib/mail/inboxHeadersSessionHydrate";
import { cancelStaggeredBodyWarm } from "@/lib/mail/bodyWarmup";
import { MAIL_LIVE_SYNC_DONE_EVENT, type MailLiveSyncDoneDetail } from "@/lib/mail/mailLiveSyncEvents";
import { isDraftSaveInFlight } from "@/lib/compose/draftSaveLock";
import { createRefreshScheduler } from "@/lib/mail/mailRefreshSchedule";
import {
  INBOX_LIST_SIZE,
  INBOX_LOAD_MORE_SIZE,
  INBOX_POST_CACHE_HIT_IMAP_DEFER_MS,
} from "@/lib/constants/inbox";
import type { MailMessageSummary } from "@/lib/types/mail";
import { MAIL_NEW_MAIL_EVENT, notifyNewMailArrived, type MailNewMailDetail } from "@/lib/mail/mailNewMailEvents";

type ListCtx = { accountId: number; folder: string; searchQuery: string };

type ReloadOptions = {
  background?: boolean;
  silent?: boolean;
};

function isListContextCurrent(ctx: ListCtx | null, accountId: number, folder: string): boolean {
  return ctx != null && ctx.accountId === accountId && ctx.folder === folder;
}

function mergeSnapshotForFolder(
  prev: MailMessageSummary[],
  snap: FolderSnapshot,
  folder: string,
  listFolderRef: string | null,
): MailMessageSummary[] {
  if (snap.folder !== folder) return prev;
  const filteredSnap = {
    ...snap,
    messages: applyOptimisticMessages(snap.accountId, folder, snap.messages),
  };
  if (filteredSnap.messages.length === 0 && snap.messages.length > 0 && snap.total > 0) {
    return applyOptimisticMessages(snap.accountId, folder, prev);
  }
  if (listFolderRef !== folder) return filteredSnap.messages;
  return mergeSnapshotWithLocal(prev, filteredSnap);
}

function mergeUniqueMessages(prev: MailMessageSummary[], batch: MailMessageSummary[]): MailMessageSummary[] {
  return mergeHeaderLists(prev, batch);
}

/** Fusion liste — ne tronque pas la pagination load-more (mail66). */
function mergeListPreservingPagination(
  prev: MailMessageSummary[],
  batch: MailMessageSummary[],
  refresh: boolean,
): MailMessageSummary[] {
  if (refresh) {
    return mergeRefreshPreservingPagination(prev, batch);
  }
  const merged = mergeHeaderLists(prev, batch);
  if (prev.length > INBOX_LIST_SIZE) {
    return merged;
  }
  return merged.slice(0, INBOX_LIST_SIZE);
}

/** Actualiser sans perdre la pagination load-more (mail66). */
function mergeRefreshPreservingPagination(
  prev: MailMessageSummary[],
  batch: MailMessageSummary[],
): MailMessageSummary[] {
  if (batch.length === 0) return prev;

  const refreshedTop = mergeRefreshBatch(prev, batch);
  if (prev.length <= INBOX_LIST_SIZE) {
    return refreshedTop.slice(0, INBOX_LIST_SIZE);
  }
  const minTopUid = Math.min(...refreshedTop.map((m) => m.uid));
  const older = prev.filter((m) => m.uid < minTopUid);
  return mergeHeaderLists(older, refreshedTop);
}

function applyPendingFlagged(messages: MailMessageSummary[], pending: Map<number, boolean>): MailMessageSummary[] {
  if (pending.size === 0) return messages;

  let changed = false;
  const next = messages.map((m) => {
    const flagged = pending.get(m.uid);
    if (flagged === undefined || m.flagged === flagged) return m;
    changed = true;
    return { ...m, flagged };
  });
  return changed ? next : messages;
}

/** Retire les pending seen confirmés par un fetch IMAP réel (pas un snapshot optimiste / DD02). */
function reconcilePendingSeenFromServer(
  messages: MailMessageSummary[],
  pendingSeen: Map<number, PendingSeenEntry>,
  imapConfirmed: boolean,
): void {
  reconcilePendingSeenEntries(pendingSeen, messages, imapConfirmed);
}

function applyPendingLocalFlags(
  messages: MailMessageSummary[],
  pendingFlagged: Map<number, boolean>,
  pendingSeen: Map<number, PendingSeenEntry>,
  skipSeenSort = false,
): MailMessageSummary[] {
  let next = applyPendingFlagged(messages, pendingFlagged);
  prunePendingSeenMap(pendingSeen);
  if (pendingSeen.size === 0) return next;

  let seenChanged = false;
  const updated = next.map((m) => {
    const seen = getPendingSeenValue(pendingSeen, m.uid);
    if (seen === undefined || m.seen === seen) return m;
    seenChanged = true;
    return { ...m, seen };
  });
  return seenChanged && !skipSeenSort ? sortInboxMessageList(updated) : updated;
}

function isFolderListSettled(snap: FolderSnapshot | null): boolean {
  if (!snap) return false;
  return snap.messages.length > 0 || snap.total === 0;
}

/** Session localStorage → bus mémoire avant navigation (évite spinner plein écran). */
function resolveNavigationSnapshot(accountId: number, folder: string): FolderSnapshot | null {
  hydrateInboxHeadersFromSession(accountId, folder);
  const snap = getFolderSnapshot(accountId, folder);
  if (!snap) return null;
  // Toujours filtrer tombstones/holds — sinon un brouillon supprimé réapparaît
  // au retour dossier (snapshot/session encore chaud avec l’UID).
  const messages = applyOptimisticMessages(accountId, folder, snap.messages);
  if (messages === snap.messages) return snap;
  return {
    ...snap,
    messages,
    total: Math.max(0, snap.total - (snap.messages.length - messages.length)),
  };
}

export function useInboxMessages(
  accountId?: number,
  folder = "INBOX",
  searchQuery = "",
  searchInput = "",
  unreadOnly = false,
) {
  const trimmedSearch = searchQuery.trim();
  const trimmedInput = searchInput.trim();
  const isSearching = trimmedSearch.length > 0;
  const isSearchMode = trimmedInput.length > 0 || isSearching;
  const isUnreadImapList = unreadOnly && !isSearching;

  const initialSnap = accountId != null && !isSearching && !unreadOnly ? getFolderSnapshot(accountId, folder) : null;

  const [messages, setMessages] = useState<MailMessageSummary[]>(() =>
    sortInboxMessageList(initialSnap?.messages ?? []),
  );
  const [total, setTotal] = useState(() => initialSnap?.total ?? 0);
  const [, setOffset] = useState(() => initialSnap?.offset ?? 0);
  const [syncing, setSyncing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fromCache, setFromCache] = useState(() => initialSnap?.fromCache ?? false);
  const [listSettled, setListSettled] = useState(() => isFolderListSettled(initialSnap));
  const fromCacheRef = useRef(fromCache);
  fromCacheRef.current = fromCache;

  const totalRef = useRef(total);
  totalRef.current = total;
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const listCtxRef = useRef<ListCtx | null>(null);
  const fetchGenRef = useRef(0);
  const navigationGenRef = useRef(0);
  const reloadNavigationGenRef = useRef(0);
  const syncDepthRef = useRef(0);
  const pendingFlaggedRef = useRef(new Map<number, boolean>());
  const pendingSeenRef = useRef(new Map<number, PendingSeenEntry>());
  /** Dossier auquel appartient l’état `messages` — évite de fusionner INBOX ↔ Brouillons (UIDs IMAP par dossier). */
  const messagesListFolderRef = useRef<string | null>(null);
  /** Plus haut UID déjà vu — détecte les arrivées (pas la pagination). */
  const maxListUidRef = useRef(0);
  const searchModeRef = useRef(false);
  const [searchResultQuery, setSearchResultQuery] = useState("");
  const prevUnreadOnlyRef = useRef<boolean | null>(null);
  const justLeftUnreadFilterRef = useRef(false);
  const backgroundSchedulerRef = useRef(createRefreshScheduler(280));
  const catchUpScheduledRef = useRef(false);
  const listSettledRef = useRef(listSettled);
  listSettledRef.current = listSettled;

  const markListSettled = useCallback((settled: boolean) => {
    listSettledRef.current = settled;
    setListSettled(settled);
    freezeDisplayOrderRef.current = settled;
    if (settled && syncDepthRef.current <= 0) {
      setSyncing(false);
    }
  }, []);

  /** Après 1er affichage trié : clic lu/non lu ne déplace plus la ligne. */
  const freezeDisplayOrderRef = useRef(false);

  const shouldFreezeDisplayOrder = useCallback(
    () => freezeDisplayOrderRef.current && listSettledRef.current && !unreadOnly && !isSearching,
    [isSearching, unreadOnly],
  );

  const applyLocalFlags = useCallback(
    (msgs: MailMessageSummary[], skipSeenSort = shouldFreezeDisplayOrder()) =>
      applyPendingLocalFlags(msgs, pendingFlaggedRef.current, pendingSeenRef.current, skipSeenSort),
    [shouldFreezeDisplayOrder],
  );

  const finalizeListOrder = useCallback(
    (prev: MailMessageSummary[], next: MailMessageSummary[]) => {
      const filteredNext = accountId != null ? applyOptimisticMessages(accountId, folder, next) : next;
      const pinned = accountId != null ? getPinnedInboxRowExitUids(accountId, folder) : undefined;
      if (prev.length === 0) {
        return applyLocalFlags(sortInboxMessageList(filteredNext), false);
      }
      // Liste déjà peinte : flags sur place, jamais de re-tri lu/non-lu (évite le flash Gmail).
      // Le tri explicite reste dans setMessagesSeen (clic utilisateur).
      if (listSettledRef.current && !unreadOnly && !isSearching) {
        const merged = mergeInboxListsPreservingOrder(prev, filteredNext, pinned);
        return applyLocalFlags(merged, true);
      }
      const merged = mergeHeaderLists(prev, filteredNext);
      const withPending = applyLocalFlags(merged, true);
      return sortInboxMessageList(withPending);
    },
    [accountId, applyLocalFlags, folder, isSearching, unreadOnly],
  );

  const hasMore = messages.length < total;

  const listUidKey = useMemo(
    () =>
      messages
        .map((m) => m.uid)
        .filter((uid) => uid > 0)
        .join(","),
    [messages],
  );

  const publishList = useCallback(
    (
      nextMessages: MailMessageSummary[],
      nextTotal: number,
      nextOffset: number,
      nextFromCache = fromCacheRef.current,
      bootstrapped = true,
    ) => {
      if (accountId == null || isSearching || unreadOnly) return;
      publishFolderSnapshot({
        accountId,
        folder,
        // Ne pas re-trier ici : l’ordre UI est figé après settle ; un sort poisonne le bus (flash Gmail).
        messages: nextMessages,
        total: nextTotal,
        offset: nextOffset,
        fromCache: nextFromCache,
        bootstrapped,
      });
      persistInboxHeadersSession(accountId, folder, sortInboxMessageList(nextMessages), nextTotal, nextOffset);
    },
    [accountId, folder, isSearching, unreadOnly],
  );

  const applySnapshot = useCallback(
    (snap: FolderSnapshot | null) => {
      if (!snap) return;
      const ctx = listCtxRef.current;
      if (!isListContextCurrent(ctx, snap.accountId, snap.folder)) return;
      if (snap.messages.length === 0 && snap.total > 0) return;

      reconcilePendingSeenFromServer(snap.messages, pendingSeenRef.current, snap.fromCache !== true);
      messagesListFolderRef.current = snap.folder;
      const filteredMessages = applyOptimisticMessages(snap.accountId, snap.folder, snap.messages);
      setMessages((prev) => finalizeListOrder(prev, filteredMessages));
      if (snap.messages.length > 0 || snap.total === 0) {
        markListSettled(true);
      }
      setTotal(snap.total);
      setOffset(snap.offset);
      setFromCache(snap.fromCache ?? false);
      setError(null);
    },
    [finalizeListOrder, markListSettled],
  );

  const beginSync = useCallback(() => {
    syncDepthRef.current += 1;
    setSyncing(true);
  }, []);

  const endSync = useCallback(() => {
    syncDepthRef.current = Math.max(0, syncDepthRef.current - 1);
    setSyncing(syncDepthRef.current > 0);
  }, []);

  const reload = useCallback(
    async (refresh = false, options: ReloadOptions = {}) => {
      const background = options.background !== false;

      if (!accountId) {
        setMessages([]);
        setTotal(0);
        setOffset(0);
        setSyncing(false);
        setFromCache(false);
        return;
      }

      const gen = ++fetchGenRef.current;
      reloadNavigationGenRef.current = navigationGenRef.current;
      const silent = options.silent === true;

      const snap = !refresh && !isSearching && !unreadOnly ? getFolderSnapshot(accountId, folder) : null;
      const hasWarmSnap = isFolderSnapshotWarm(snap);

      if (refresh && !isSearching && !unreadOnly) {
        clearFolderSnapshot(accountId, folder);
      }

      if (!silent) {
        beginSync();
      }

      try {
        const res = await fetchInboxMessages({
          accountId,
          folder,
          limit: INBOX_LIST_SIZE,
          offset: 0,
          refresh,
          q: isSearching ? trimmedSearch : undefined,
          unseen: isUnreadImapList,
        });

        if (gen !== fetchGenRef.current) {
          return;
        }

        if (!res.ok) {
          if (!background && !hasWarmSnap) {
            setError(res.message);
          }
          /* Toujours sortir du spinner si la liste est vide (échec IMAP OAuth inclus). */
          if (messagesRef.current.length === 0) {
            markListSettled(true);
          }
          return;
        }

        const batch = filterHeldMutationMessages(accountId, folder, res.data.messages ?? []);
        const nextTotal = res.data.total ?? 0;
        let mergedList = batch;

        if (batch.length === 0 && nextTotal > 0 && !isSearching && !unreadOnly && messagesRef.current.length === 0) {
          markListSettled(false);
          void (async () => {
            beginSync();
            const WARM_TIMEOUT_MS = 45_000;
            try {
              const ok = await Promise.race([
                warmFolderMessages(accountId, folder),
                new Promise<false>((resolve) => {
                  window.setTimeout(() => resolve(false), WARM_TIMEOUT_MS);
                }),
              ]);
              if (listSettledRef.current) return;
              const warmSnap = getFolderSnapshot(accountId, folder);
              if (warmSnap?.messages.length) {
                applySnapshot(warmSnap);
                return;
              }
              if (!ok) {
                setError("Connexion boîte mail trop longue — réessayez Actualiser.");
                await reload(true, { background: true, silent: true });
                const retrySnap = getFolderSnapshot(accountId, folder);
                if (retrySnap?.messages.length) {
                  setError(null);
                  applySnapshot(retrySnap);
                  return;
                }
              }
            } finally {
              endSync();
              if (!listSettledRef.current) {
                markListSettled(true);
              }
            }
          })();
        } else if (batch.length > 0 || nextTotal === 0) {
          markListSettled(true);
        }

        const cacheLabel = res.data.cache ?? (refresh ? "miss" : "hit");
        const imapConfirmed = refresh || cacheLabel === "miss" || cacheLabel === "bypass";
        reconcilePendingSeenFromServer(batch, pendingSeenRef.current, imapConfirmed);

        setMessages((prev) => {
          const ctx = listCtxRef.current;
          if (!isListContextCurrent(ctx, accountId, folder)) return prev;

          const folderChangedDuringFetch = reloadNavigationGenRef.current !== navigationGenRef.current;
          const prevFromOtherFolder = messagesListFolderRef.current !== folder;

          if (
            batch.length === 0 &&
            prev.length > 0 &&
            !isSearching &&
            !unreadOnly &&
            !folderChangedDuringFetch &&
            !prevFromOtherFolder
          ) {
            return applyOptimisticMessages(accountId, folder, applyLocalFlags(prev));
          }

          const replaceWithBatchOnly = isSearching || unreadOnly || folderChangedDuringFetch || prevFromOtherFolder;

          const preserveOrder = !replaceWithBatchOnly && listSettledRef.current && prev.length > 0;

          mergedList = replaceWithBatchOnly
            ? sortInboxMessageList(batch)
            : preserveOrder
              ? mergeInboxListsPreservingOrder(prev, batch)
              : mergeListPreservingPagination(prev, batch, refresh);
          messagesListFolderRef.current = folder;
          const listed = applyLocalFlags(mergedList, true);
          const withPending =
            isSearching || unreadOnly ? listed : applyOptimisticMessages(accountId, folder, listed);
          mergedList = withPending;
          if (preserveOrder) {
            return withPending;
          }
          return sortInboxMessageList(withPending);
        });
        mergedList = applyLocalFlags(mergedList, true);
        if (!listSettledRef.current || isSearching || unreadOnly) {
          mergedList = sortInboxMessageList(mergedList);
        }
        setTotal((prevTotal) => Math.max(prevTotal, nextTotal));
        setOffset((prevOffset) => Math.max(prevOffset, mergedList.length));
        setFromCache(isSearching || unreadOnly ? false : res.data.cache === "hit" || res.data.cache === "partial-hit");
        setError(null);

        if (isSearching) {
          setSearchResultQuery(trimmedSearch);
        }

        if (!isSearching && !unreadOnly) {
          publishList(
            mergedList,
            Math.max(totalRef.current, nextTotal),
            mergedList.length,
            res.data.cache === "hit" || res.data.cache === "partial-hit",
          );
        }
      } finally {
        if (!silent) {
          endSync();
        } else if (gen === fetchGenRef.current && listSettledRef.current) {
          setSyncing(false);
        }
      }
    },
    [
      accountId,
      beginSync,
      endSync,
      folder,
      isSearching,
      isUnreadImapList,
      publishList,
      trimmedSearch,
      unreadOnly,
      shouldFreezeDisplayOrder,
      markListSettled,
      applySnapshot,
    ],
  );

  const reloadRef = useRef(reload);
  reloadRef.current = reload;

  const setMessagesSeen = useCallback(
    (uids: number[], seen: boolean, options?: { force?: boolean; localOnly?: boolean }) => {
      if (accountId == null) return;

      const force = options?.force === true;
      const localOnly = options?.localOnly === true;
      const valid = [...new Set(uids.filter((uid) => uid > 0))];
      if (valid.length === 0) return;

      for (const uid of valid) {
        setPendingSeenEntry(pendingSeenRef.current, uid, seen);
      }

      const prev = messagesRef.current;
      const uidSet = new Set(valid);
      const shouldSync =
        force ||
        valid.some((uid) => !prev.some((m) => m.uid === uid)) ||
        valid.some((uid) => {
          const msg = prev.find((m) => m.uid === uid);
          return msg != null && msg.seen !== seen;
        });

      // Après 1er paint : jamais re-trier au clic (flash). Filtre « Non lus » : on garde l’ordre local.
      const preserveOrder = listSettledRef.current && !isSearching;

      let next = prev;
      let listChanged = false;
      for (const uid of valid) {
        const idx = next.findIndex((m) => m.uid === uid);
        if (idx === -1) continue;
        if (next[idx].seen === seen) continue;
        listChanged = true;
        next = next.map((m) => (m.uid === uid ? { ...m, seen } : m));
      }

      const ordered = !listChanged || preserveOrder || unreadOnly ? next : sortInboxMessageList(next);
      const applied = applyLocalFlags(ordered, true);
      messagesRef.current = applied;
      setMessages(applied);

      if (listChanged && !isSearching) {
        // fromCache: true — ne pas confirmer/clear le pending via le bus.
        patchFolderSnapshotSeen(accountId, folder, valid, seen);
        // Ouverture / lu : autoriser la pastille à descendre sous IMAP tout de suite
        // (sans attendre listSettled ni un repli de compte).
        markFolderListViewedThisSession(accountId, folder);
        if (!unreadOnly) {
          publishList(applied, totalRef.current, applied.length, true);
        }
      }

      if (!shouldSync || localOnly) return;

      const apiCall = seen
        ? markMailMessageSeen({ accountId, folder, uids: valid })
        : markMailMessageUnread({ accountId, folder, uids: valid });

      void apiCall.then((res) => {
        if (res.ok) {
          // Renouvelle le hold : un fetch lancé avant le clic ne doit pas gagner.
          for (const uid of valid) {
            setPendingSeenEntry(pendingSeenRef.current, uid, seen);
          }
          patchFolderSnapshotSeen(accountId, folder, valid, seen);
          return;
        }

        for (const uid of valid) {
          pendingSeenRef.current.delete(uid);
        }
        setMessages((current) => {
          let reverted = current;
          for (const uid of valid) {
            if (!uidSet.has(uid)) continue;
            const idx = reverted.findIndex((m) => m.uid === uid);
            if (idx === -1) continue;
            reverted = reverted.map((m) => (m.uid === uid ? { ...m, seen: !seen } : m));
          }
          const appliedRevert = applyLocalFlags(reverted, true);
          if (!isSearching && !unreadOnly) {
            publishList(appliedRevert, totalRef.current, appliedRevert.length, true);
          }
          return appliedRevert;
        });
      });
    },
    [accountId, folder, isSearching, publishList, unreadOnly, applyLocalFlags],
  );

  const applyLocalSeenFlags = useCallback(
    (uids: number[], seen: boolean) => {
      setMessagesSeen(uids, seen, { localOnly: true });
    },
    [setMessagesSeen],
  );

  const markMessageUnread = useCallback(
    (uid: number, options?: { force?: boolean }) => {
      setMessagesSeen([uid], false, options);
    },
    [setMessagesSeen],
  );

  const removeMessages = useCallback(
    (uids: number[]) => {
      if (accountId == null || uids.length === 0) return;

      const validUids = uids.filter((uid) => uid > 0);
      if (validUids.length === 0) return;

      tombstoneUids(accountId, folder, validUids);
      patchFolderSnapshotRemoveUids(accountId, folder, validUids);

      const drop = new Set(validUids);
      setMessages((prev) => {
        const next = prev.filter((m) => !drop.has(m.uid));
        if (next.length === prev.length) return prev;

        const removed = prev.length - next.length;
        const nextTotal = Math.max(0, totalRef.current - removed);
        setTotal(nextTotal);
        setOffset(next.length);
        if (!isSearching) {
          publishList(next, nextTotal, next.length, false, true);
        }
        return next;
      });
    },
    [accountId, folder, isSearching, publishList],
  );

  const removeMessage = useCallback(
    (uid: number) => {
      removeMessages([uid]);
    },
    [removeMessages],
  );

  const markMessageSeen = useCallback(
    (uid: number, options?: { force?: boolean }) => {
      setMessagesSeen([uid], true, options);
    },
    [setMessagesSeen],
  );

  const markMessageFlagged = useCallback(
    (uid: number, flagged: boolean) => {
      if (accountId == null || uid <= 0) return;

      pendingFlaggedRef.current.set(uid, flagged);

      setMessages((prev) => {
        const idx = prev.findIndex((m) => m.uid === uid);
        if (idx === -1) return applyLocalFlags(prev);
        if (prev[idx].flagged === flagged) {
          return applyLocalFlags(prev);
        }

        const next = [...prev];
        next[idx] = { ...next[idx], flagged };
        const applied = applyLocalFlags(next);
        if (!isSearching && !unreadOnly) {
          publishList(applied, totalRef.current, applied.length);
        }
        return applied;
      });

      void markMailMessageFlagged({ accountId, folder, uid, flagged }).then((res) => {
        if (pendingFlaggedRef.current.get(uid) === flagged) {
          pendingFlaggedRef.current.delete(uid);
        }
        if (res.ok) return;

        pendingFlaggedRef.current.delete(uid);
        setMessages((prev) => {
          const idx = prev.findIndex((m) => m.uid === uid);
          if (idx === -1) return prev;
          const next = [...prev];
          next[idx] = { ...next[idx], flagged: !flagged };
          if (!isSearching && !unreadOnly) {
            publishList(next, totalRef.current, next.length);
          }
          return next;
        });
      });
    },
    [accountId, folder, isSearching, unreadOnly, publishList],
  );

  const refreshInBackground = useCallback(() => {
    if (!accountId || isSearching || isDraftSaveInFlight() || hasMutationHold(accountId, folder)) {
      return;
    }

    backgroundSchedulerRef.current.schedule(`bg:${accountId}:${folder}`, () => {
      void (async () => {
        await syncFolderHeaders(accountId, folder);
        const snap = getFolderSnapshot(accountId, folder);
        if (snap) {
          applySnapshot(snap);
        }
        await reload(false, { background: true, silent: true });
      })();
    });
  }, [accountId, applySnapshot, folder, isSearching, reload]);

  const pullInboxNow = useCallback(async () => {
    if (!accountId || isSearching || unreadOnly) return;

    markListSettled(false);
    beginSync();
    try {
      await syncFolderHeaders(accountId, folder);
      const snapAfterSync = getFolderSnapshot(accountId, folder);
      if (snapAfterSync?.messages.length) {
        applySnapshot(snapAfterSync);
      }
      await reload(true, { background: false, silent: true });
      const snapFinal = getFolderSnapshot(accountId, folder);
      if (snapFinal) {
        applySnapshot(snapFinal);
      }
    } finally {
      endSync();
    }
  }, [accountId, applySnapshot, beginSync, endSync, folder, isSearching, reload, unreadOnly]);

  const loadMore = useCallback(async () => {
    if (!accountId || loadingMore || !hasMore) {
      return;
    }

    const nextOffset = messagesRef.current.length;
    const gen = fetchGenRef.current;
    setLoadingMore(true);
    const res = await fetchInboxMessages({
      accountId,
      folder,
      limit: INBOX_LOAD_MORE_SIZE,
      offset: nextOffset,
      refresh: true,
      q: isSearching ? trimmedSearch : undefined,
    });

    if (gen !== fetchGenRef.current) {
      setLoadingMore(false);
      return;
    }

    if (res.ok) {
      const batch = filterHeldMutationMessages(accountId, folder, res.data.messages ?? []);
      const ctx = listCtxRef.current;
      if (isListContextCurrent(ctx, accountId, folder) && messagesListFolderRef.current === folder) {
        const merged = mergeUniqueMessages(messagesRef.current, batch);
        const nextTotal = res.data.total ?? totalRef.current;

        setTotal(Math.max(nextTotal, merged.length));

        setOffset(merged.length);
        messagesListFolderRef.current = folder;
        const listed = applyLocalFlags(merged);
        setMessages(
          isSearching || unreadOnly ? listed : applyOptimisticMessages(accountId, folder, listed)
        );

        if (!isSearching) {
          publishList(merged, nextTotal, merged.length);
        }
      }
    } else {
      setError(res.message);
    }
    setLoadingMore(false);
  }, [accountId, folder, hasMore, isSearching, loadingMore, publishList, trimmedSearch]);

  const visibleMessages = useMemo(
    () => (unreadOnly ? messages.filter((m) => !m.seen) : messages),
    [messages, unreadOnly],
  );

  const visibleTotal = unreadOnly ? visibleMessages.length : total;
  const visibleHasMore = unreadOnly ? false : hasMore;

  useLayoutEffect(() => {
    if (accountId == null) {
      listCtxRef.current = null;
      pendingSeenRef.current.clear();
      freezeDisplayOrderRef.current = false;
      listSettledRef.current = true;
      messagesListFolderRef.current = null;
      setMessages([]);
      setTotal(0);
      setOffset(0);
      setSyncing(false);
      setFromCache(false);
      setError(null);
      /* settled=true → pas de spinner « Chargement… » infini sans compte IMAP. */
      setListSettled(true);
      searchModeRef.current = false;
      return;
    }

    const prev = listCtxRef.current;
    const nowInSearch = isSearchMode;
    const isNavigation =
      prev !== null && (prev.accountId !== accountId || prev.folder !== folder || prev.searchQuery !== trimmedSearch);

    if (isNavigation) {
      fetchGenRef.current += 1;
      navigationGenRef.current += 1;
      syncDepthRef.current = 0;
      pendingFlaggedRef.current.clear();
      freezeDisplayOrderRef.current = false;
      messagesListFolderRef.current = null;
      const folderChanged = prev !== null && (prev.accountId !== accountId || prev.folder !== folder);
      if (folderChanged && prev !== null) {
        cancelStaggeredBodyWarm(prev.accountId, prev.folder);
        markListSettled(false);
        // Pending seen est indexé par UID seul — au changement de compte, ne pas
        // appliquer le « lu » d’un compte sur l’autre. Le sticky DD02 (10 min) couvre le retour.
        if (prev.accountId !== accountId) {
          pendingSeenRef.current.clear();
        }
      }
      // Bloque tout de suite les callbacks du dossier précédent (ex. INBOX → Brouillons).
      listCtxRef.current = { accountId, folder, searchQuery: trimmedSearch };

      if (folderChanged) {
        if (!nowInSearch && !unreadOnly) {
          const navSnap = resolveNavigationSnapshot(accountId, folder);
          if (navSnap?.messages.length) {
            messagesListFolderRef.current = folder;
            setMessages(applyLocalFlags(sortInboxMessageList(navSnap.messages), false));
            markListSettled(true);
            setTotal(navSnap.total);
            setOffset(navSnap.offset);
            setFromCache(navSnap.fromCache ?? false);
            setSyncing(true);
          } else {
            setMessages([]);
            setTotal(0);
            setOffset(0);
            setFromCache(false);
            markListSettled(false);
            setSyncing(true);
          }
        } else {
          setMessages([]);
          setTotal(0);
          setOffset(0);
          setFromCache(false);
          setSyncing(true);
        }
      } else {
        setSyncing(false);
      }
    } else {
      listCtxRef.current = { accountId, folder, searchQuery: trimmedSearch };
    }

    const enteringSearch = nowInSearch && !searchModeRef.current;
    searchModeRef.current = nowInSearch;

    if (nowInSearch) {
      const folderOrAccountChanged = prev !== null && (prev.accountId !== accountId || prev.folder !== folder);
      if (enteringSearch || folderOrAccountChanged) {
        setMessages([]);
        setTotal(0);
        setOffset(0);
        setFromCache(false);
      }
      setSyncing(true);
      return;
    }

    if (!isSearching) {
      setSearchResultQuery("");
    }

    if (unreadOnly) {
      messagesListFolderRef.current = null;
      setMessages([]);
      setTotal(0);
      setOffset(0);
      setFromCache(false);
      setError(null);
      setSyncing(true);
      return;
    }

    if (!isNavigation) {
      const snap = resolveNavigationSnapshot(accountId, folder);
      if (snap?.messages.length) {
        messagesListFolderRef.current = folder;
        setMessages(applyLocalFlags(sortInboxMessageList(snap.messages), false));
        markListSettled(true);
        setTotal(snap.total);
        setOffset(snap.offset);
        setFromCache(snap.fromCache ?? false);
        setSyncing(true);
      } else if (!nowInSearch && !unreadOnly) {
        setSyncing(true);
      }
    }
    setError(null);
  }, [accountId, folder, isSearchMode, trimmedSearch, unreadOnly]);

  useEffect(() => {
    if (accountId == null || isSearching || unreadOnly) return;

    return subscribeFolderSnapshot(accountId, folder, (snap) => {
      const ctx = listCtxRef.current;
      if (!isListContextCurrent(ctx, accountId, folder)) return;
      if (snap.folder !== folder) return;
      if (snap.messages.length === 0 && snap.total > 0) return;

      reconcilePendingSeenFromServer(snap.messages, pendingSeenRef.current, snap.fromCache !== true);
      setMessages((prev) => {
        if (!isListContextCurrent(listCtxRef.current, accountId, folder)) return prev;

        const merged = mergeSnapshotForFolder(prev, snap, folder, messagesListFolderRef.current);
        messagesListFolderRef.current = folder;
        return finalizeListOrder(prev, merged);
      });
      if (snap.messages.length > 0 || snap.total === 0) {
        markListSettled(true);
      }
      setTotal((prevTotal) => (snap.total < prevTotal ? snap.total : Math.max(prevTotal, snap.total)));
      setOffset((prevOffset) => Math.max(prevOffset, snap.offset, snap.messages.length));
      setFromCache(snap.fromCache ?? false);
      setError(null);
    });
  }, [accountId, folder, isSearching, unreadOnly, finalizeListOrder, markListSettled]);

  useEffect(() => {
    if (accountId == null || isSearching || unreadOnly || !listSettled) return;
    markFolderListViewedThisSession(accountId, folder);
  }, [accountId, folder, isSearching, unreadOnly, listSettled]);

  useEffect(() => {
    if (accountId == null || isSearchMode) return;

    const wasUnread = prevUnreadOnlyRef.current;
    prevUnreadOnlyRef.current = unreadOnly;

    if (wasUnread === null) {
      if (unreadOnly) {
        setMessages([]);
        setTotal(0);
        setOffset(0);
        setFromCache(false);
        setSyncing(true);
        void reloadRef.current(false, { background: false, silent: false });
      }
      return;
    }

    if (wasUnread === unreadOnly) return;

    fetchGenRef.current += 1;

    if (unreadOnly) {
      setMessages([]);
      setTotal(0);
      setOffset(0);
      setFromCache(false);
      setSyncing(true);
      void reloadRef.current(false, { background: false, silent: false });
      return;
    }

    justLeftUnreadFilterRef.current = true;
    markListSettled(false);

    const snap = getFolderSnapshot(accountId, folder);
    if (snap?.messages.length) {
      applySnapshot(snap);
      markListSettled(true);
    } else {
      setMessages([]);
      setTotal(0);
      setOffset(0);
      setFromCache(false);
      setSyncing(true);
      void reloadRef.current(false, { background: false, silent: false });
    }
  }, [accountId, applySnapshot, folder, isSearchMode, unreadOnly]);

  useEffect(() => {
    if (accountId == null) return;
    if (isSearchMode || unreadOnly) return;

    if (justLeftUnreadFilterRef.current) {
      justLeftUnreadFilterRef.current = false;
      markListSettled(false);
      const snap = resolveNavigationSnapshot(accountId, folder);
      if (snap?.messages.length) {
        applySnapshot(snap);
      }
      void (async () => {
        await syncFolderHeaders(accountId, folder);
        await reloadRef.current(true, { background: true, silent: true });
      })();
      return;
    }

    const navGen = navigationGenRef.current;

    const runStaleWhileRevalidate = async (deferImapMs = 0) => {
      if (deferImapMs > 0) {
        await idleDelay(deferImapMs);
        if (navigationGenRef.current !== navGen) return;
      }
      await syncFolderHeaders(accountId, folder);
      if (navigationGenRef.current !== navGen) return;
      await reloadRef.current(false, { background: true, silent: true });
      if (navigationGenRef.current === navGen) {
        setSyncing(false);
      }
    };

    hydrateInboxHeadersFromSession(accountId, folder);
    let snap = getFolderSnapshot(accountId, folder);
    if (snap?.messages.length) {
      applySnapshot(snap);
      markListSettled(true);
      void runStaleWhileRevalidate();
      return;
    }

    void (async () => {
      const cacheResult = await syncFolderFromServerCache(accountId, folder);
      if (navigationGenRef.current !== navGen) return;

      snap = getFolderSnapshot(accountId, folder);
      if (snap?.messages.length) {
        applySnapshot(snap);
        markListSettled(true);
        await runStaleWhileRevalidate(cacheResult === "hit" ? INBOX_POST_CACHE_HIT_IMAP_DEFER_MS : 0);
        return;
      }

      if (navigationGenRef.current !== navGen) return;
      beginSync();
      try {
        await reloadRef.current(false, { background: true, silent: true });
      } finally {
        if (navigationGenRef.current === navGen) {
          endSync();
        }
      }
    })();
  }, [accountId, applySnapshot, beginSync, endSync, folder, isSearchMode, unreadOnly]);

  useEffect(() => {
    if (accountId == null) return;
    if (trimmedInput.length > 0 && trimmedInput !== trimmedSearch) {
      setSyncing(true);
    }
  }, [accountId, trimmedInput, trimmedSearch]);

  useEffect(() => {
    if (accountId == null || trimmedSearch === "") return;
    void reloadRef.current(false, { background: false, silent: false });
  }, [accountId, folder, trimmedSearch]);

  useEffect(() => {
    maxListUidRef.current = 0;
    catchUpScheduledRef.current = false;
  }, [accountId, folder]);

  useEffect(() => {
    if (accountId == null || isSearching || unreadOnly) return;
    if (catchUpScheduledRef.current || listUidKey.length === 0) return;

    catchUpScheduledRef.current = true;
    scheduleBodyCatchUp(accountId, folder, messagesRef.current);
  }, [accountId, folder, isSearching, listUidKey, unreadOnly]);

  useEffect(() => {
    if (accountId == null || isSearching || unreadOnly) return;

    const uids = messages.map((m) => m.uid).filter((uid) => uid > 0);
    if (uids.length === 0) return;

    const maxUid = Math.max(...uids);

    /**
     * Baseline cold start / hydrate DD02 : verrouiller le max sans pending-reveal.
     * Sinon une 2ᵉ vague (50→200) ou un reload marque toute la fenêtre comme « new mail »
     * → liste vidée puis retour compte-gouttes.
     */
    if (maxListUidRef.current === 0 || !listSettledRef.current) {
      maxListUidRef.current = Math.max(maxListUidRef.current, maxUid);
      return;
    }

    if (maxUid <= maxListUidRef.current) return;

    const newUids = uids.filter((uid) => uid > maxListUidRef.current);
    maxListUidRef.current = maxUid;

    // Vague massive = sync/hydrate, pas une arrivée temps réel (celle-ci passe par sync IMAP).
    if (newUids.length === 0 || newUids.length > 8) return;

    notifyNewMailArrived(accountId, folder, newUids.length, newUids);
  }, [accountId, folder, isSearching, messages, unreadOnly]);

  useEffect(() => {
    if (accountId == null || isSearching || unreadOnly) return;

    const onLiveSyncDone = (event: Event) => {
      const detail = (event as CustomEvent<MailLiveSyncDoneDetail>).detail;
      if (!detail || detail.accountId !== accountId || detail.folder !== folder) return;
      if (isDraftSaveInFlight()) return;

      const snap = getFolderSnapshot(accountId, folder);
      if (snap) {
        applySnapshot(snap);
      }
      // Pas de refreshInBackground ici : le poll vient déjà de sync cache+IMAP.
      // Un 2ᵉ passage faisait disparaître le mail puis relançait une préparation.
    };

    window.addEventListener(MAIL_LIVE_SYNC_DONE_EVENT, onLiveSyncDone);
    return () => window.removeEventListener(MAIL_LIVE_SYNC_DONE_EVENT, onLiveSyncDone);
  }, [accountId, applySnapshot, folder, isSearching, unreadOnly]);

  useEffect(() => {
    if (accountId == null || isSearching || unreadOnly) return;

    const onNewMail = (event: Event) => {
      const detail = (event as CustomEvent<MailNewMailDetail>).detail;
      if (!detail || detail.accountId !== accountId || detail.folder !== folder) return;
      if (isDraftSaveInFlight()) return;

      const snap = getFolderSnapshot(accountId, folder);
      if (snap) {
        applySnapshot(snap);
      }
      // Idem : warm déjà lancé par notifyNewMailArrived — pas de 2ᵉ syncHeaders+reload.
    };

    window.addEventListener(MAIL_NEW_MAIL_EVENT, onNewMail);
    return () => window.removeEventListener(MAIL_NEW_MAIL_EVENT, onNewMail);
  }, [accountId, applySnapshot, folder, isSearching, unreadOnly]);

  const loadingInitial = !isSearching && !unreadOnly && visibleMessages.length === 0 && !listSettled;

  return {
    messages: visibleMessages,
    total: visibleTotal,
    loading: loadingInitial,
    loadingInitial,
    listSettled,
    syncing,
    loadingMore,
    hasMore: visibleHasMore,
    error,
    fromCache,
    reload,
    pullInboxNow,
    refreshInBackground,
    setListSyncing: (active: boolean) => {
      if (active) beginSync();
      else endSync();
    },
    removeMessage,
    removeMessages,
    markMessageSeen,
    markMessageUnread,
    setMessagesSeen,
    applyLocalSeenFlags,
    markMessageFlagged,
    loadMore,
    searchResultQuery,
  };
}
